import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stack,
  Divider,
  Box,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Search as SearchIcon, X as CancelIcon } from 'react-feather';
import AddIcon from '@mui/icons-material/AddOutlined';
import theme from 'src/theme';
import FilterSelectInput from './FilterSelectInput';
import ListRefreshButton from './ListRefreshButton';
import ColumnSearch from './ColumnSearch';

const FMSToolbar = ({
  placeholder,
  onSearch,
  searchValue,
  showSearch = true,
  actionName,
  onAction,
  groupLabel,
  groupType,
  groupData,
  showGroup,
  onGroupChange,
  hideAction,
  actionLoading,
  onRefresh,
  refreshing = false,
  children,
  actionIcon = <AddIcon />,
  actionButtonSize = 'large',
  actionButtonSx = {},
  sx = {},
  searchFields,
  searchValues,
  onSearchFields,
  onClearSearchFields,
  hideActionDivider = false,
  rightChildren,
  className,
}) => {
  const [searchText, setSearchText] = useState('');
  const [curSearch, setCurSearch] = useState('');

  useEffect(() => {
    if (typeof searchValue !== 'string') return;
    setSearchText((currentValue) => (currentValue === searchValue ? currentValue : searchValue));
    setCurSearch((currentValue) => (currentValue === searchValue ? currentValue : searchValue));
  }, [searchValue]);

  const onSearchChange = (e) => {
    const searchStr = e.target.value;
    setSearchText(searchStr);
    if (searchStr === '' && curSearch) {
      onSearch('');
    }
  };

  const onClearSearch = () => {
    setSearchText('');
    if (curSearch) {
      setCurSearch('');
      onSearch('');
    }
  };

  const handleSearch = () => {
    setCurSearch(searchText);
    onSearch(searchText);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleGroupChange = (value) => {
    setCurSearch('');
    setSearchText('');
    onGroupChange(value);
  };

  const renderSearchBar = () => (
    <TextField
      size="small"
      placeholder={placeholder}
      value={searchText}
      variant="outlined"
      onChange={onSearchChange}
      onKeyDown={handleKeyDown}
      sx={{
        width: { md: '320px', xs: '100%' },
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          backgroundColor: theme.palette.grey[50],
          border: `1.5px solid ${theme.palette.grey[300]}`,
          transition: 'all 0.3s ease',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {/* Separator */}
            <Box
              sx={{
                width: '1px',
                height: 28,
                bgcolor: theme.palette.divider,
                mx: 1,
              }}
            />
            {/* Search Button */}
            <IconButton onClick={handleSearch}>
              <SearchIcon color={theme.palette.primary.main} size={16} />
            </IconButton>
            {searchText && (
              <IconButton onClick={onClearSearch}>
                <CancelIcon size={16} />
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
  return (
    <Card
      className={className}
      sx={{
        borderRadius: 2,
        background: '#FFFFFF',
        boxShadow: 'none',
        overflow: 'hidden',

        border: `1px solid ${theme.palette.divider}`,

        // Gradient border trick

        ...sx
      }}
    >
      {/* Card content goes here */}
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          spacing={2}
          justifyContent="space-between"
          flexWrap={{ xs: 'nowrap', md: 'wrap', lg: 'nowrap' }}
          useFlexGap
        >
          {/* Left Section */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            flex={1}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
            flexWrap={{ xs: 'nowrap', md: 'wrap', lg: 'nowrap' }}
            useFlexGap
            minWidth={0}
          >
            {showSearch && (searchFields?.length
              ? (
                <ColumnSearch
                  fields={searchFields}
                  values={searchValues}
                  onApply={onSearchFields}
                  onClear={onClearSearchFields}
                  containerSx={{
                    width: { xs: '100%', md: 320 },
                    flexShrink: 0
                  }}
                  sx={{ width: { xs: '100%', md: 320 } }}
                />
              )
              : renderSearchBar())}
            {onRefresh && <ListRefreshButton refreshing={refreshing} onRefresh={onRefresh} />}

            {showGroup && (
            <FilterSelectInput
              label={groupLabel}
              onInputChange={handleGroupChange}
              data={groupData}
              value={groupType}
            />
            )}
            {!rightChildren && children}
          </Stack>

          {/* Divider (only on desktop for better separation) */}
          {!hideAction && !hideActionDivider && (
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: 'none', md: 'block' } }}
          />
          )}

          {/* Action Button */}
          {(rightChildren || !hideAction) && (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            flexWrap={{ xs: 'nowrap', md: 'wrap', lg: 'nowrap' }}
            useFlexGap
            sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}
          >
            {rightChildren}
            {!hideAction && (
            <Stack sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}>
              {actionLoading ? (
                <CircularProgress size={28} />
              ) : (
                <Button
                  size={actionButtonSize}
                  variant="contained"
                  startIcon={actionIcon}
                  onClick={onAction}
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    px: { xs: 2, md: 3 },
                    py: { xs: 1, md: 1.2 },
                    fontWeight: 600,
                    fontSize: 15,
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap',
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&:hover': {
                      background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                    },
                    ...actionButtonSx,
                  }}
                >
                  {actionName}
                </Button>
              )}
            </Stack>
            )}
          </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

FMSToolbar.propTypes = {
  placeholder: PropTypes.string,
  searchValue: PropTypes.string,
  actionName: PropTypes.string,
  groupLabel: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  actionIcon: PropTypes.element,
  actionButtonSize: PropTypes.oneOf(['small', 'medium', 'large']),
  actionButtonSx: PropTypes.object,
  sx: PropTypes.object,
  groupType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  groupData: PropTypes.array,
  showGroup: PropTypes.bool,
  showSearch: PropTypes.bool,
  actionLoading: PropTypes.bool,
  hideAction: PropTypes.bool,
  onSearch: PropTypes.func,
  searchFields: PropTypes.array,
  searchValues: PropTypes.object,
  onSearchFields: PropTypes.func,
  onClearSearchFields: PropTypes.func,
  hideActionDivider: PropTypes.bool,
  rightChildren: PropTypes.node,
  className: PropTypes.string,
  onAction: PropTypes.func,
  onGroupChange: PropTypes.func,
  onRefresh: PropTypes.func,
  refreshing: PropTypes.bool,
};

export default FMSToolbar;
